import { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useExperimentExposedEvent } from '@zenbusiness/application-commons-clickstream';
import Banner from '@zenbusiness/zui/Banner';
import DismissBanner from '@zenbusiness/zui/DismissBanner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import { ErrorBoundary } from '#client/components/ErrorBoundary';
import { useBanners } from '#client/hooks/useBanners';

import * as Styled from './HomeBanners.style';

function HomeBanners({ onOpenModal }) {
  const { data, error, inWfcPastDueBannerCtaExperiment, loading } = useBanners();
  const banner = data?.[0];
  const featureFlagKey = banner?.experiment?.featureFlagKey;
  const featureFlagVariation = banner?.experiment?.featureFlagVariation;
  const isExperimentBanner = Boolean(featureFlagKey && featureFlagVariation);
  const isDismissed = banner?.dismissible && banner?.storageKey && localStorage.getItem(banner.storageKey) === 'true';
  const trackExperimentExposed = useExperimentExposedEvent({
    featureFlagKey,
    featureFlagVariation,
    inExperiment: inWfcPastDueBannerCtaExperiment,
  });

  const handleCtaClick = useCallback(
    (cta = {}) => {
      if (cta.modalName) {
        onOpenModal(cta.modalName);
      }
    },
    [onOpenModal],
  );

  useEffect(() => {
    if (!loading && !error && isExperimentBanner && !isDismissed) {
      trackExperimentExposed();
    }
  }, [error, isDismissed, isExperimentBanner, loading, trackExperimentExposed]);

  if (loading || error) {
    return null;
  }

  if (!banner) {
    return null;
  }

  const experimentDynamicProps = isExperimentBanner ? { featureFlagKey, featureFlagVariation } : undefined;

  const createAction = (cta) => ({
    label: cta.label,
    ...(experimentDynamicProps && {
      clickstreamProps: {
        dynamicProperties: experimentDynamicProps,
      },
    }),
    ...(cta.modalName && { onClick: () => handleCtaClick(cta) }),
    ...(cta.url && { href: cta.url }),
  });

  const slotProps = {
    ...(banner.primaryCta && { primaryAction: createAction(banner.primaryCta) }),
    ...(banner.secondaryCta && { secondaryAction: createAction(banner.secondaryCta) }),
  };

  const slots = {
    ...(banner.imageUrl && {
      startIcon: <SharedAsset alt="start-icon-banner" src={banner.imageUrl} />,
    }),
  };

  const BannerComponent = banner.dismissible ? DismissBanner : Banner;

  return (
    <Styled.HomeBanners>
      <ErrorBoundary id="src/Home#Banners">
        <BannerComponent
          clickstreamProps={{
            appSection: 'home',
            ...(experimentDynamicProps && {
              dynamicProperties: experimentDynamicProps,
            }),
          }}
          title={banner.title}
          subtitle={banner.description}
          variant={banner.type}
          slotProps={slotProps}
          slots={slots}
          {...(banner.dismissible && banner.storageKey && { storageKey: banner.storageKey })}
        />
      </ErrorBoundary>
    </Styled.HomeBanners>
  );
}

HomeBanners.propTypes = {
  onOpenModal: PropTypes.func,
};

export default HomeBanners;
